import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCartCheckout as CheckoutIcon,
  RemoveShoppingCart as EmptyCartIcon,
  Receipt as CheckIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DeliverActionIcon from '../components/icons/DeliverActionIcon';
import CertificateLink from '../components/CertificateLink';
import { useAuth } from '../context/AuthContext';
import { useOpenOrdersCount } from '../context/OpenOrdersCountContext';
import { useCart } from '../context/CartContext';
import { ROLES } from '../constants/roles';
import { hasPermission } from '../utils/permissions';
import { DIAMOND_TYPES, ORDER_MODES } from '../constants/appConstants';
import { roundCarat2dp } from '../utils/orderItemDisplay';
import {
  buildCheckoutPayload,
  canEditCartItemPrice,
  canEditCartItemWeight,
  canShowStaffDeliverActions,
  canShowStaffCheckoutToCheck,
  computeCartTotalsByCurrency,
  executeStaffCheckoutFlow,
  getCartItemCurrency,
  getCartItemDefaultPricePerUnit,
  getCartItemDisplayTotal,
  getCartItemEffectivePricePerUnit,
  getFailedCartIndices,
  isCartStaffPriceEditable,
  isCartWeightEditable,
} from '../utils/cartStaffCheckout';
import axios from 'axios';
import { getNonCertOrderItemDisplayLabel } from '../utils/nonCertLotDisplay';
import { useTranslation } from 'react-i18next';
import AutocompleteField from '../components/inputs/AutocompleteField';
import ClientTagsRow from '../components/clients/ClientTagsRow';
import AddCustomItemDialog from '../components/AddCustomItemDialog';
import { formatCurrency } from '../utils/currency';
import { isCustomByWeightItem } from '../utils/orderItemDisplay';
import { weightUnitShort } from '../utils/customItemPricing';
import { CUSTOM_PRICE_TYPES } from '../constants/appConstants';
import { useFeedback } from '../context/FeedbackContext';
import { buildCheckoutLeftoverRows } from '../utils/buildCheckoutLeftovers';
import { buttonStartIcon } from '../utils/buttonLoading';

const Cart = () => {
  const { user, userRole, isInternal, isClient, customPermissions } = useAuth();
  const { refreshOpenOrdersCount } = useOpenOrdersCount();
  const { cartItems, removeFromCart, clearCart, addToCart, retainItemsByIndices, updateCartItem } = useCart();
  const { showError, showSuccess } = useFeedback();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientNote, setClientNote] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [addCustomDialogOpen, setAddCustomDialogOpen] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState(new Set());

  useEffect(() => {
    setSelectedIndices(new Set(cartItems.map((_, i) => i)));
  }, [cartItems]);

  const selectedCartEntries = useMemo(
    () => cartItems.map((item, index) => ({ item, index })).filter(({ index }) => selectedIndices.has(index)),
    [cartItems, selectedIndices],
  );

  const toggleCartSelect = (index) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIndices.size === cartItems.length) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(cartItems.map((_, i) => i)));
    }
  };

  const canPlaceForClient = isInternal && (
    userRole === ROLES.MANAGER || hasPermission(userRole, 'reserve_for_client', customPermissions)
  );
  const canAddCustomItem = canPlaceForClient;
  const canStaffDeliverActions = canShowStaffDeliverActions({ isInternal, userRole, customPermissions });
  const canStaffCheckoutToCheck = canShowStaffCheckoutToCheck({ isInternal, userRole, customPermissions });
  const canEditPriceInCart = canEditCartItemPrice({ isInternal, userRole, customPermissions });
  const canEditCartWeight = canEditCartItemWeight({ isInternal, userRole, customPermissions });
  const showItemNoteField = isInternal || isClient;

  const getCartItemNoteFieldProps = (item, index) => {
    if (isClient) {
      return {
        value: item.clientItemNote || '',
        onChange: (e) => updateCartItem(index, { clientItemNote: e.target.value }),
        label: t('cart.itemNoteForStaff'),
        placeholder: t('cart.itemNoteForStaffPlaceholder'),
      };
    }
    return {
      value: item.staffNote || '',
      onChange: (e) => updateCartItem(index, { staffNote: e.target.value }),
      label: t('cart.itemNoteForHost'),
      placeholder: t('cart.itemNoteForHostPlaceholder'),
    };
  };

  /** Client whose pricingRule drives cart preview totals (staff: selected client; client: self). */
  const cartPricingClient = canPlaceForClient ? selectedClient : (isClient ? user : null);

  useEffect(() => {
    if (canPlaceForClient) {
      fetchClients();
    }
  }, [canPlaceForClient]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`/api/users?role=${encodeURIComponent(ROLES.CLIENT)}`);
      setClients((response.data || []).filter((u) => !u.disabled));
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleCheckout = async () => {
    await runStaffCheckout({ markDelivered: false, createCheck: false });
  };

  const handleMarkDelivered = async () => {
    await runStaffCheckout({ markDelivered: true, createCheck: false });
  };

  const handleCheckoutToCheck = async () => {
    await runStaffCheckout({ markDelivered: true, createCheck: true });
  };

  const runStaffCheckout = async ({ markDelivered, createCheck }) => {
    if (selectedCartEntries.length === 0) return;
    if (canPlaceForClient && !selectedClient) return;

    setCheckoutLoading(true);

    try {
      const checkoutItems = selectedCartEntries.map(({ item }) => item);
      const { order, checkId, itemErrors } = await executeStaffCheckoutFlow(axios, {
        payload: buildCheckoutPayload(checkoutItems, {
          canPlaceForClient,
          selectedClient,
          clientNote,
          canEditCartItemPrice: canEditPriceInCart,
          isClient,
        }),
        markDelivered,
        createCheck,
        clientUserId: selectedClient?._id,
      });

      if (!order?._id) {
        showError(t('cart.checkoutFailed'));
        return;
      }

      const failedCheckoutIndices = getFailedCartIndices(itemErrors);
      const failedOriginalIndices = failedCheckoutIndices.map((i) => selectedCartEntries[i]?.index).filter((i) => i != null);
      const indicesToKeep = cartItems
        .map((_, i) => i)
        .filter((i) => !selectedIndices.has(i) || failedOriginalIndices.includes(i));

      const checkoutLeftovers = buildCheckoutLeftoverRows({
        selectedCartEntries,
        itemErrors,
        cartItems,
        selectedIndices,
        getItemLabel,
        getItemDetails,
        t,
      });

      const navState = checkoutLeftovers.length > 0 ? { checkoutLeftovers } : undefined;

      if (indicesToKeep.length > 0) {
        retainItemsByIndices(indicesToKeep);
      } else {
        clearCart();
        setClientNote('');
      }
      setSelectedIndices(new Set(indicesToKeep));
      refreshOpenOrdersCount();

      const orderRef = order._id.slice(-6).toUpperCase();
      let successMsg;
      if (markDelivered) {
        successMsg = t('cart.orderDeliveredSuccess', {
          count: order.itemCount,
          ref: orderRef,
          name: `${selectedClient?.firstName || ''} ${selectedClient?.lastName || ''}`.trim(),
        });
      } else {
        successMsg = t('cart.orderCreatedSuccess', { count: order.itemCount, ref: orderRef });
      }

      if (checkId) {
        setClientNote('');
        showSuccess(successMsg);
        navigate(`/cashiering/${checkId}`, navState ? { state: navState } : undefined);
        return;
      }

      showSuccess(successMsg);
      navigate(`/orders/${order._id}`, navState ? { state: navState } : undefined);
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      const itemErrors = error.response?.data?.itemErrors || [];
      if (itemErrors.length > 0) {
        showError([msg, ...itemErrors.map((e) => e.error)].filter(Boolean).join(' '));
      } else {
        showError(msg);
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getItemLabel = (item) => {
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      return item.description || t('cart.customItem');
    }
    if (item.type === DIAMOND_TYPES.CERTIFIED) {
      const d = item.diamond;
      return d?.certificateNumber || '—';
    }
    return getNonCertOrderItemDisplayLabel(item);
  };

  /** Full reference info: cert details, custom description, or lot label. */
  const getItemReferenceLabel = (item) => {
    if (item.type === DIAMOND_TYPES.CERTIFIED) {
      const d = item.diamond;
      const parts = [d?.shape, d?.carat ? `${d.carat}ct` : null, d?.color, d?.clarity].filter(Boolean);
      return parts.join(' ');
    }
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      if (isCustomByWeightItem(item)) {
        return `${item.description || t('cart.customItem')} (${weightUnitShort(item.weightUnit)})`;
      }
      return item.description || t('cart.customItem');
    }
    return getNonCertOrderItemDisplayLabel(item);
  };

  /** Weight display for the weight column. */
  const getItemWeightDisplay = (item) => {
    if (item.type === DIAMOND_TYPES.CERTIFIED) {
      const carat = item.diamond?.carat;
      return carat != null ? `${carat} ct` : '—';
    }
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      if (isCustomByWeightItem(item) && item.weight != null) {
        return `${item.weight} ${weightUnitShort(item.weightUnit)}`;
      }
      return '—';
    }
    if (item.weight != null) {
      return `${roundCarat2dp(item.weight)} ct`;
    }
    return '—';
  };

  const getItemTypeLabel = (item) => {
    if (item.type === DIAMOND_TYPES.CUSTOM) return t('cart.custom');
    if (item.type === DIAMOND_TYPES.CERTIFIED) return t('cart.certified');
    return t('cart.nonCertified');
  };

  const getItemTypeColor = (item) => {
    if (item.type === DIAMOND_TYPES.CUSTOM) return 'secondary';
    if (item.type === DIAMOND_TYPES.CERTIFIED) return 'primary';
    return 'default';
  };

  const getItemLab = (item) => item?.diamond?.lab;

  const getItemDetails = (item) => {
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      if (item.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT && item.weight != null) {
        const unit = weightUnitShort(item.weightUnit);
        return `${item.weight} ${unit} @ ${formatCurrency(item.pricePerUnit, item.currency)}/${unit}`;
      }
      return t('cart.customFixedPrice');
    }
    if (item.type === DIAMOND_TYPES.CERTIFIED) {
      const d = item.diamond;
      return `${d?.shape || ''} ${d?.carat || ''}ct ${d?.color || ''} ${d?.clarity || ''}`.trim();
    }
    if (item.orderMode === ORDER_MODES.BY_COUNT && item.stoneCount) {
      return t('cart.stonesWeightEstimate', {
        count: item.stoneCount,
        weight: roundCarat2dp(item.weight),
      });
    }
    return `${roundCarat2dp(item.weight)} ct @ ${formatCurrency(getCartItemEffectivePricePerUnit(item, cartPricingClient) ?? item.lot?.pricePerCarat, getCartItemCurrency(item))}/ct`;
  };

  const getItemPrice = (item) => {
    const currency = getCartItemCurrency(item);
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      if (item.priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT) {
        return formatCurrency(
          Math.round(getCartItemDisplayTotal(item, cartPricingClient) * 100) / 100,
          item.currency,
        );
      }
      return formatCurrency(item.finalPrice, item.currency);
    }
    const total = getCartItemDisplayTotal(item, cartPricingClient);
    return formatCurrency(Math.round(total * 100) / 100, currency);
  };

  const getItemPricePerCt = (item) => {
    const currency = getCartItemCurrency(item);
    if (item.type === DIAMOND_TYPES.CUSTOM) {
      if (isCustomByWeightItem(item) && item.pricePerUnit != null) {
        return `${formatCurrency(item.pricePerUnit, item.currency)}/${weightUnitShort(item.weightUnit)}`;
      }
      return '—';
    }
    const ppu = getCartItemEffectivePricePerUnit(item, cartPricingClient);
    if (ppu == null) return '—';
    return `${formatCurrency(ppu, currency)}/ct`;
  };

  const getCartPriceInputValue = (item) => {
    if (item.pricePerUnit !== undefined && item.pricePerUnit !== null && item.pricePerUnit !== '') {
      return String(item.pricePerUnit);
    }
    const defaultPpu = getCartItemDefaultPricePerUnit(item, cartPricingClient);
    return defaultPpu != null ? String(defaultPpu) : '';
  };

  const handleCartPriceChange = (index, rawValue) => {
    const item = cartItems[index];
    const defaultPpu = getCartItemDefaultPricePerUnit(item, cartPricingClient);
    if (rawValue === '') {
      updateCartItem(index, { pricePerUnit: undefined });
      return;
    }
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed) || parsed < 0) return;
    if (defaultPpu != null && parsed === defaultPpu) {
      updateCartItem(index, { pricePerUnit: undefined });
      return;
    }
    updateCartItem(index, { pricePerUnit: parsed });
  };

  const handleCartWeightChange = (index, rawValue) => {
    if (rawValue === '') return;
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed) || parsed <= 0) return;
    const item = cartItems[index];
    if (item.type === DIAMOND_TYPES.NON_CERTIFIED) {
      updateCartItem(index, { weight: roundCarat2dp(parsed) });
      return;
    }
    updateCartItem(index, { weight: parsed });
  };

  const renderCartWeightInput = (item, index) => {
    const suffix = item.type === DIAMOND_TYPES.CUSTOM
      ? weightUnitShort(item.weightUnit)
      : 'ct';
    return (
      <Box display="inline-flex" alignItems="center" gap={0.5}>
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0.01, step: item.type === DIAMOND_TYPES.NON_CERTIFIED ? 0.01 : 'any' }}
          value={item.weight ?? ''}
          onChange={(e) => handleCartWeightChange(index, e.target.value)}
          sx={{ width: 72, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }}
          aria-label={t('orderItems.columns.weight')}
        />
        <Typography variant="body2" component="span">{suffix}</Typography>
      </Box>
    );
  };

  const renderWeightCell = (item, index) => {
    if (canEditCartWeight && isCartWeightEditable(item)) {
      return renderCartWeightInput(item, index);
    }
    return getItemWeightDisplay(item);
  };

  const renderPricePerCtCell = (item, index) => {
    if (canEditPriceInCart && isCartStaffPriceEditable(item)) {
      return (
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={getCartPriceInputValue(item)}
          onChange={(e) => handleCartPriceChange(index, e.target.value)}
          sx={{ width: 100 }}
        />
      );
    }
    return getItemPricePerCt(item);
  };

  const cartTotalsByCurrency = computeCartTotalsByCurrency(
    selectedCartEntries.map(({ item }) => item),
    cartPricingClient,
  );

  const selectedCount = selectedCartEntries.length;

  const renderCartTotals = (variant = 'subtitle1', size = undefined) => {
    if (cartTotalsByCurrency.length === 0) return null;
    if (cartTotalsByCurrency.length === 1) {
      const { currency, total } = cartTotalsByCurrency[0];
      return (
        <Typography variant={variant} color="primary.main" fontWeight={600} fontSize={size}>
          {t('cart.total')}: {formatCurrency(total, currency)}
        </Typography>
      );
    }
    return (
      <Stack spacing={0.25}>
        {cartTotalsByCurrency.map(({ currency, total }) => (
          <Typography key={currency} variant={variant} color="primary.main" fontWeight={600} fontSize={size}>
            {t('cart.totalCurrency', { currency })}: {formatCurrency(total, currency)}
          </Typography>
        ))}
      </Stack>
    );
  };

  const renderAddCustomItemButton = () => (
    canAddCustomItem ? (
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setAddCustomDialogOpen(true)}
      >
        {t('cart.addCustomItem')}
      </Button>
    ) : null
  );

  if (cartItems.length === 0) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box textAlign="center" py={8}>
            <EmptyCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>{t('cart.emptyTitle')}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('cart.emptyBody', { page: isClient ? t('cart.clientPortal') : t('cart.inventory') })}
            </Typography>
            {renderAddCustomItemButton()}
          </Box>
          <AddCustomItemDialog
            open={addCustomDialogOpen}
            onClose={() => setAddCustomDialogOpen(false)}
            onAddToCart={addToCart}
            title={t('cart.addCustomItem')}
            submitLabel={t('cart.addCustomItemToCart')}
          />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {t('cart.title')} ({cartItems.length})
            </Typography>
            {cartItems.length > 0 && renderCartTotals()}
          </Box>
          {cartItems.length > 0 && (
            <Button size="small" color="error" onClick={clearCart} startIcon={<DeleteIcon />}>
              {t('cart.clearCart')}
            </Button>
          )}
          {renderAddCustomItemButton()}
        </Box>

        {cartItems.length > 0 && (
          <>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={cartItems.length > 0 && selectedIndices.size === cartItems.length}
                      indeterminate={selectedIndices.size > 0 && selectedIndices.size < cartItems.length}
                      onChange={toggleSelectAll}
                    />
                  )}
                  label={t('cart.selectAll')}
                />
                {cartItems.map((item, index) => (
                  <Card key={index} elevation={2} variant={selectedIndices.has(index) ? 'elevation' : 'outlined'} sx={{ opacity: selectedIndices.has(index) ? 1 : 0.65 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box display="flex" gap={1} flex={1} minWidth={0}>
                          <Checkbox
                            size="small"
                            checked={selectedIndices.has(index)}
                            onChange={() => toggleCartSelect(index)}
                            sx={{ p: 0, mt: 0.25 }}
                          />
                          <Box flex={1} minWidth={0}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Chip
                              label={getItemTypeLabel(item)}
                              size="small"
                              color={getItemTypeColor(item)}
                            />
                          </Box>
                          {item.type === DIAMOND_TYPES.CERTIFIED ? (
                            <Box sx={{ mb: 0.25 }}>
                              <CertificateLink
                                certificateNumber={getItemLabel(item)}
                                lab={getItemLab(item)}
                                variant="subtitle1"
                                sx={{ fontWeight: 'bold' }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {getItemReferenceLabel(item)}
                              </Typography>
                            </Box>
                          ) : (
                            <>
                              <Typography variant="subtitle1" fontWeight="bold">{getItemReferenceLabel(item)}</Typography>
                            </>
                          )}
                          <Typography variant="body2" color="text.secondary" component="div">
                            {renderWeightCell(item, index)}
                          </Typography>
                          <Box mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            {canEditPriceInCart && isCartStaffPriceEditable(item) ? (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" color="text.secondary">{t('cart.pricePerCt')}</Typography>
                                {renderPricePerCtCell(item, index)}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">{getItemPricePerCt(item)}</Typography>
                            )}
                            <Typography variant="body1" color="primary.main" fontWeight="bold">
                              {getItemPrice(item)}
                            </Typography>
                          </Box>
                          {showItemNoteField && (
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              minRows={1}
                              maxRows={3}
                              sx={{ mt: 1 }}
                              {...getCartItemNoteFieldProps(item, index)}
                              inputProps={{ maxLength: 1000 }}
                            />
                          )}
                          </Box>
                        </Box>
                        <IconButton color="error" size="small" onClick={() => removeFromCart(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={cartItems.length > 0 && selectedIndices.size === cartItems.length}
                          indeterminate={selectedIndices.size > 0 && selectedIndices.size < cartItems.length}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>{t('cart.type')}</TableCell>
                      <TableCell>{t('cart.reference')}</TableCell>
                      <TableCell>{t('orderItems.columns.weight')}</TableCell>
                      <TableCell>{t('cart.pricePerCt')}</TableCell>
                      <TableCell>{t('cart.price')}</TableCell>
                      {showItemNoteField && (
                        <TableCell>{isInternal ? t('cart.itemNoteForHost') : t('cart.itemNoteForStaff')}</TableCell>
                      )}
                      <TableCell align="right">{t('cart.remove')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cartItems.map((item, index) => (
                      <TableRow key={index} hover selected={selectedIndices.has(index)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedIndices.has(index)}
                            onChange={() => toggleCartSelect(index)}
                          />
                        </TableCell>
                        <TableCell>
                        <Chip
                              label={getItemTypeLabel(item)}
                              size="small"
                              color={getItemTypeColor(item)}
                            />
                        </TableCell>
                        <TableCell>
                          {item.type === DIAMOND_TYPES.CERTIFIED ? (
                            <Box>
                              <CertificateLink certificateNumber={getItemLabel(item)} lab={getItemLab(item)} variant="body2" />
                              <Typography variant="caption" color="text.secondary" display="block">
                                {getItemReferenceLabel(item)}
                              </Typography>
                            </Box>
                          ) : (
                            getItemReferenceLabel(item)
                          )}
                        </TableCell>
                        <TableCell>{renderWeightCell(item, index)}</TableCell>
                        <TableCell>{renderPricePerCtCell(item, index)}</TableCell>
                        <TableCell>{getItemPrice(item)}</TableCell>
                        {showItemNoteField && (
                          <TableCell sx={{ minWidth: 180 }}>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={3}
                              {...getCartItemNoteFieldProps(item, index)}
                              inputProps={{ maxLength: 1000 }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Tooltip title={t('cart.remove')}>
                            <IconButton color="error" size="small" onClick={() => removeFromCart(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 600, borderTop: 2, borderColor: 'divider' }}>
                        {cartTotalsByCurrency.length > 1 ? t('cart.orderTotal') : t('cart.total')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.main', borderTop: 2, borderColor: 'divider' }}>
                        {cartTotalsByCurrency.length === 1 ? (
                          formatCurrency(cartTotalsByCurrency[0].total, cartTotalsByCurrency[0].currency)
                        ) : (
                          <Stack spacing={0.5}>
                            {cartTotalsByCurrency.map(({ currency, total }) => (
                              <Typography key={currency} variant="body2" fontWeight={700}>
                                {t('cart.totalCurrency', { currency })}: {formatCurrency(total, currency)}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      {isInternal && <TableCell sx={{ borderTop: 2, borderColor: 'divider' }} />}
                      <TableCell sx={{ borderTop: 2, borderColor: 'divider' }} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Checkout section */}
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>{t('cart.orderTotal')}</Typography>
                {renderCartTotals('h6', '1.25rem')}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {canPlaceForClient && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>{t('cart.selectClient')}</Typography>
                  <AutocompleteField
                    options={clients}
                    value={selectedClient?._id || ''}
                    onChange={(nextId) => setSelectedClient(clients.find((c) => c._id === nextId) || null)}
                    label={t('cart.client')}
                    getOptionLabel={(u) => {
                      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
                      const company = u.companyName ? ` (${u.companyName})` : '';
                      const phone = u.phone ? ` (${u.phone})` : '';
                      return `${name}${company}${phone}`;
                    }}
                    getOptionValue={(u) => u._id}
                    isOptionEqualToValue={(opt, val) => opt._id === val._id}
                    size="small"
                    sx={{ maxWidth: 400 }}
                  />
                  {selectedClient && (selectedClient.clientTags || []).length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        {`${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim()}
                      </Typography>
                      <ClientTagsRow tags={selectedClient.clientTags} />
                    </Box>
                  )}
                </Box>
              )}

              <Box mb={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={5}
                  size="small"
                  label={t('cart.noteForEmployee')}
                  placeholder={t('cart.noteForEmployeePlaceholder')}
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                />
              </Box>

              <Stack spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={buttonStartIcon(checkoutLoading, CheckoutIcon)}
                  onClick={handleCheckout}
                  disabled={checkoutLoading || selectedCount === 0 || (canPlaceForClient && !selectedClient)}
                  fullWidth
                >
                  {checkoutLoading
                    ? t('cart.processing')
                    : isClient
                      ? t('cart.reserveItems', { count: selectedCount })
                      : selectedClient
                        ? t('cart.placeOrderForClient', {
                            name: `${selectedClient.firstName} ${selectedClient.lastName}`,
                            count: selectedCount,
                          })
                        : t('cart.selectClientFirst')}
                </Button>

                {(canStaffDeliverActions || canStaffCheckoutToCheck) && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
                      {t('cart.staffQuickActions')}
                    </Typography>
                    {canStaffDeliverActions && (
                      <Button
                        variant="outlined"
                        size="large"
                        color="warning"
                        startIcon={buttonStartIcon(checkoutLoading, DeliverActionIcon, { fontSize: 'inherit' })}
                        onClick={handleMarkDelivered}
                        disabled={checkoutLoading || selectedCount === 0 || !selectedClient}
                        fullWidth
                      >
                        {checkoutLoading ? t('cart.processing') : t('cart.markDeliveredForClient')}
                      </Button>
                    )}
                    {canStaffCheckoutToCheck && (
                      <Button
                        variant="outlined"
                        size="large"
                        color="secondary"
                        startIcon={buttonStartIcon(checkoutLoading, CheckIcon)}
                        onClick={handleCheckoutToCheck}
                        disabled={checkoutLoading || selectedCount === 0 || !selectedClient}
                        fullWidth
                      >
                        {checkoutLoading ? t('cart.processing') : t('cart.checkoutToCheck')}
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          </>
        )}
      </Container>
      <AddCustomItemDialog
        open={addCustomDialogOpen}
        onClose={() => setAddCustomDialogOpen(false)}
        onAddToCart={addToCart}
        title={t('cart.addCustomItem')}
        submitLabel={t('cart.addCustomItemToCart')}
      />
    </Layout>
  );
};

export default Cart;
